import React, { useState, useEffect } from 'react';
import { initThemeFromLogo } from './theme';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import apiService from './services/api';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    initThemeFromLogo(`${process.env.PUBLIC_URL}/logo.png`);
    const token = localStorage.getItem('login_token');
    const userData = localStorage.getItem('user_data');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('login_token');
        localStorage.removeItem('user_data');
      }
    }
  }, []);

  const handleLogin = async (credentials) => {
    setLoading(true);
    try {
      const response = await apiService.verifyUserDetails(credentials);
      
      if (response && response.errorCode === 200) {
        // Login successful - errorCode 200 means user verified
        const user = {
          userId: response.userId,
          userFirstName: response.userFirstName,
          userLastName: response.userLastName,
          eMailId: response.eMailId,
          userRole: response.userRole,
          status: response.status,
          login_token: response.login_token,
          accountID: response.accountID,
          managerId: response.managerId,
          binanceFuturesApiKey: response.binanceFuturesApiKey,
          binanceFuturesSecretKey: response.binanceFuturesSecretKey,
          tradingAccountList: response.tradingAccountList || []
        };

        // Store complete user data and token in localStorage
        localStorage.setItem('login_token', response.login_token);
        localStorage.setItem('user_data', JSON.stringify(user));
        
        setCurrentUser(user);
        setIsAuthenticated(true);
        return true;
      } else {
        // Login failed
        console.error('Login failed:', response.errorMessage || 'Invalid credentials');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear stored data
    localStorage.removeItem('login_token');
    localStorage.removeItem('user_data');
    
    // Reset state
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const handleCreateUser = async (userData) => {
    try {
      const response = await apiService.createUser(userData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to create user' };
      }
    } catch (error) {
      console.error('Create user error:', error);
      return { success: false, error: 'Failed to create user' };
    }
  };

  const handleGetUsersList = async (filters = {}) => {
    try {
      console.log('App.js - handleGetUsersList called with filters:', filters);
      const requesterId = currentUser?.userId;
      const isAdmin = currentUser?.userRole === 3;
      const response = isAdmin
        ? await apiService.getUsersList(filters)
        : (requesterId
            ? await apiService.getScopedUsersList(requesterId)
            : await apiService.getUsersList(filters));
      console.log('App.js - handleGetUsersList response:', response);
      
      if (response && Array.isArray(response)) {
        console.log('App.js - Returning success with data length:', response.length);
        return { success: true, data: response };
      } else {
        console.log('App.js - Response is not an array:', response);
        return { success: false, error: 'Failed to fetch users' };
      }
    } catch (error) {
      console.error('App.js - Get users list error:', error);
      return { success: false, error: 'Failed to fetch users' };
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      console.log('App.js - Updating user with data:', userData);
      const response = await apiService.updateUserDetails(userData);
      console.log('App.js - Update API response:', response);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to update user' };
      }
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: 'Failed to update user' };
    }
  };

  // Trading Account handlers
  const handleCreateTradingAccount = async (tradingAccountData) => {
    try {
      const response = await apiService.createTradingAccount(tradingAccountData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to create trading account' };
      }
    } catch (error) {
      console.error('Create trading account error:', error);
      return { success: false, error: 'Failed to create trading account' };
    }
  };

  const handleGetTradingAccountList = async (filters = {}) => {
    try {
      const response = await apiService.getTradingAccountList(filters);
      
      if (response && Array.isArray(response)) {
        return { success: true, data: response };
      } else {
        return { success: false, error: 'Failed to fetch trading accounts' };
      }
    } catch (error) {
      console.error('Get trading account list error:', error);
      return { success: false, error: 'Failed to fetch trading accounts' };
    }
  };

  const handleGetTradingAccountListAll = async (filters = {}) => {
    try {
      const response = await apiService.getTradingAccountListAll(filters);
      
      if (response && Array.isArray(response)) {
        return { success: true, data: response };
      } else {
        return { success: false, error: 'Failed to fetch all trading accounts' };
      }
    } catch (error) {
      console.error('Get all trading account list error:', error);
      return { success: false, error: 'Failed to fetch all trading accounts' };
    }
  };

  const handleDeleteTradingAccount = async (tradingAccountData) => {
    try {
      const response = await apiService.deleteTradingAccount(tradingAccountData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to delete trading account' };
      }
    } catch (error) {
      console.error('Delete trading account error:', error);
      return { success: false, error: 'Failed to delete trading account' };
    }
  };

  // Symbol handlers
  const handleCreateSymbol = async (symbolData) => {
    try {
      const response = await apiService.createSymbol(symbolData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to create symbol' };
      }
    } catch (error) {
      console.error('Create symbol error:', error);
      return { success: false, error: 'Failed to create symbol' };
    }
  };

  const handleUpdateSymbol = async (symbolData) => {
    try {
      const response = await apiService.updateSymbol(symbolData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to update symbol' };
      }
    } catch (error) {
      console.error('Update symbol error:', error);
      return { success: false, error: 'Failed to update symbol' };
    }
  };

  const handleGetSymbolList = async (filters = {}) => {
    try {
      const response = await apiService.getSymbolList(filters);
      
      if (response && Array.isArray(response)) {
        return { success: true, data: response };
      } else {
        return { success: false, error: 'Failed to fetch symbols' };
      }
    } catch (error) {
      console.error('Get symbol list error:', error);
      return { success: false, error: 'Failed to fetch symbols' };
    }
  };

  const handleGetSymbolListAll = async (filters = {}) => {
    try {
      const response = await apiService.getSymbolListAll(filters);
      
      if (response && Array.isArray(response)) {
        return { success: true, data: response };
      } else {
        return { success: false, error: 'Failed to fetch all symbols' };
      }
    } catch (error) {
      console.error('Get all symbol list error:', error);
      return { success: false, error: 'Failed to fetch all symbols' };
    }
  };

  const handleDeleteSymbol = async (symbolData) => {
    try {
      const response = await apiService.deleteSymbol(symbolData);
      
      if (response && response.errorCode === 200) {
        return { success: true, data: response };
      } else {
        return { success: false, error: response.errorMessage || 'Failed to delete symbol' };
      }
    } catch (error) {
      console.error('Delete symbol error:', error);
      return { success: false, error: 'Failed to delete symbol' };
    }
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} loading={loading} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard 
                  onLogout={handleLogout} 
                  currentUser={currentUser}
                  onCreateUser={handleCreateUser}
                  onGetUsersList={handleGetUsersList}
                  onUpdateUser={handleUpdateUser}
                  onCreateTradingAccount={handleCreateTradingAccount}
                  onGetTradingAccountList={handleGetTradingAccountList}
                  onGetTradingAccountListAll={handleGetTradingAccountListAll}
                  onDeleteTradingAccount={handleDeleteTradingAccount}
                  onCreateSymbol={handleCreateSymbol}
                  onUpdateSymbol={handleUpdateSymbol}
                  onGetSymbolList={handleGetSymbolList}
                  onGetSymbolListAll={handleGetSymbolListAll}
                  onDeleteSymbol={handleDeleteSymbol}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route 
            path="/" 
            element={<Navigate to="/login" replace />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;


